import React from "react";
import { Sparkles, Droplets, ThumbsUp, ShieldCheck, Home as HomeIcon, Building2, Building, Sun, Heart, Leaf, BadgeCheck, ArrowRight, Phone, MessageCircle, HeartHandshake } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingForm from "../components/BookingForm";
import PricingSection from "../components/PricingSection";
import ServiceArea from "../components/ServiceArea";
import StickyDiscount from "../components/StickyDiscount";
import { useLang } from "../context/LanguageContext";

const HERO_IMG = "https://images.unsplash.com/photo-1724582586529-62622e50c0b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxwcmlzdGluZSUyMG1vZGVybiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzc5NjM3NzQxfDA&ixlib=rb-4.1.0&q=85";
const OFFICE_IMG = "https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBkZXNrJTIwY2xlYW58ZW58MHx8fHwxNzc5NjM3NzQxfDA&ixlib=rb-4.1.0&q=85";
const CLEANER_IMG = "https://images.pexels.com/photos/9462206/pexels-photo-9462206.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WINDOW_IMG = "https://images.unsplash.com/photo-1551524163-d00af9f12253?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxjbGVhbiUyMHdpbmRvdyUyMGJlYXV0aWZ1bCUyMHZpZXd8ZW58MHx8fHwxNzc5NjM3NzQxfDA&ixlib=rb-4.1.0&q=85";

function TrustChip({ icon: Icon, label, testId }) {
    return (
        <div className="flex items-center gap-2.5" data-testid={testId}>
            <div className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center text-[#16A34A]">
                <Icon className="w-5 h-5" />
            </div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#1A365D]">{label}</span>
        </div>
    );
}

function ServiceCard({ icon: Icon, title, desc, image, accent = false, testId }) {
    return (
        <div
            className={`group relative overflow-hidden rounded-3xl border ${accent ? "border-[#16A34A]/20 bg-gradient-to-br from-[#F0FDF4] to-white" : "border-slate-200 bg-white"} p-8 hover:-translate-y-1 transition-transform duration-300 shadow-lg shadow-slate-200/40`}
            data-testid={testId}
        >
            {image ? (
                <div className="relative h-44 -mx-8 -mt-8 mb-6 overflow-hidden">
                    <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A365D]/40 via-transparent to-transparent" />
                </div>
            ) : null}
            <div className="inline-flex w-12 h-12 rounded-2xl bg-[#1A365D] text-white items-center justify-center mb-4">
                <Icon className="w-6 h-6" />
            </div>
            <h3 className="font-display font-bold text-xl text-[#1A365D] mb-2">{title}</h3>
            <p className="text-sm text-[#64748B] leading-relaxed">{desc}</p>
        </div>
    );
}

export default function Home() {
    const { t } = useLang();

    return (
        <div className="min-h-screen bg-white" data-testid="home-page">
            <Header />

            {/* HERO */}
            <section className="relative pt-10 lg:pt-16 pb-10 lg:pb-14 overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0 bg-grain opacity-60" />
                <div className="absolute top-20 -right-32 w-[480px] h-[480px] rounded-full bg-[#16A34A]/10 blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-[420px] h-[420px] rounded-full bg-[#1A365D]/10 blur-3xl" />

                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-5xl animate-fade-up">
                        <span className="overline mb-6 inline-block">{t("hero.tagline")}</span>
                        <div className="mb-3">
                            <img
                                src="/privathilfe-logo.png"
                                alt="Privathilfe.ch — Hand in Hand mit Herzen"
                                className="h-32 sm:h-44 lg:h-56 w-auto select-none object-contain"
                                draggable={false}
                            />
                        </div>
                        <div className="font-display font-bold text-base sm:text-lg text-[#16A34A] tracking-wide -mt-2 mb-6">Hand in Hand mit Herzen</div>
                        <p className="text-lg text-[#64748B] max-w-2xl leading-relaxed">{t("hero.subtitle")}</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a href="#booking" className="btn-primary" data-testid="hero-book-button">
                                {t("hero.cta_book")} <ArrowRight className="w-4 h-4" />
                            </a>
                            <a href="#offer" className="btn-secondary" data-testid="hero-offer-button">
                                {t("hero.cta_offer")}
                            </a>
                        </div>
                        <div className="mt-9 flex flex-wrap gap-x-8 gap-y-5">
                            <TrustChip icon={Sparkles} label={t("hero.trust1")} testId="trust-streifenfrei" />
                            <TrustChip icon={Droplets} label={t("hero.trust2")} testId="trust-gruendlich" />
                            <TrustChip icon={ShieldCheck} label={t("hero.trust3")} testId="trust-professionell" />
                            <TrustChip icon={ThumbsUp} label={t("hero.trust4")} testId="trust-zuverlaessig" />
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section id="services" className="py-16 lg:py-24" data-testid="services-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="max-w-3xl mb-10 lg:mb-14">
                        <span className="overline mb-4 inline-block">{t("services.overline")}</span>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-[#1A365D] tracking-tight">{t("services.title")}</h2>
                        <p className="mt-5 text-lg text-[#64748B]">{t("services.subtitle")}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
                        <ServiceCard icon={HomeIcon} title={t("services.privat.title")} desc={t("services.privat.desc")} image={HERO_IMG} testId="service-card-privat" />
                        <ServiceCard icon={Sun} title={t("services.scheiben.title")} desc={t("services.scheiben.desc")} image={WINDOW_IMG} accent testId="service-card-scheiben" />
                        <ServiceCard icon={HeartHandshake} title={t("services.begleit.title")} desc={t("services.begleit.desc")} image={CLEANER_IMG} testId="service-card-begleit" />
                        <ServiceCard icon={Building2} title={t("services.buero.title")} desc={t("services.buero.desc")} image={OFFICE_IMG} testId="service-card-buero" />
                        <ServiceCard icon={Building} title={t("services.gebaeude.title")} desc={t("services.gebaeude.desc")} image={HERO_IMG} testId="service-card-gebaeude" />
                    </div>
                </div>
            </section>

            {/* VALUES STRIP */}
            <section className="py-16 bg-[#F8FAFC]" data-testid="values-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-8">
                    {[
                        { icon: Heart, title: t("values.v1_title"), desc: t("values.v1_desc") },
                        { icon: Leaf, title: t("values.v2_title"), desc: t("values.v2_desc") },
                        { icon: BadgeCheck, title: t("values.v3_title"), desc: t("values.v3_desc") },
                    ].map((v, i) => (
                        <div key={i} className="flex items-start gap-4" data-testid={`value-${i}`}>
                            <div className="w-12 h-12 rounded-2xl bg-white shadow-sm border border-slate-200 flex items-center justify-center text-[#16A34A] flex-shrink-0">
                                <v.icon className="w-5 h-5" />
                            </div>
                            <div>
                                <h4 className="font-display font-bold text-lg text-[#1A365D] mb-1.5">{v.title}</h4>
                                <p className="text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* PRICING */}
            <PricingSection />

            {/* SPECIAL OFFER */}
            <section id="offer" className="py-24 lg:py-32" data-testid="offer-section">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden bg-[#1A365D] text-white p-8 sm:p-10 md:p-16 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
                        <div className="absolute -top-32 -right-32 w-[400px] h-[400px] rounded-full bg-[#16A34A]/30 blur-3xl" />
                        <div className="relative">
                            <span className="inline-block px-4 py-1.5 rounded-full bg-[#16A34A] text-white text-xs font-bold uppercase tracking-wider mb-6">
                                {t("offer.badge")}
                            </span>
                            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1]">
                                {t("offer.title")}
                            </h2>
                            <p className="mt-5 text-lg text-white/80 max-w-md">{t("offer.subtitle")}</p>

                            <div className="mt-8 flex flex-wrap items-baseline gap-4">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-white/60 font-semibold">{t("offer.from")}</div>
                                    <div className="text-xl sm:text-2xl font-display font-bold text-white/40 line-through">CHF 200.–</div>
                                </div>
                                <ArrowRight className="w-5 h-5 text-[#4ADE80]" />
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-[#4ADE80] font-bold">{t("offer.to")}</div>
                                    <div className="text-4xl sm:text-5xl font-display font-black text-[#4ADE80]">CHF 120.–</div>
                                </div>
                            </div>
                            <a href="#booking" className="btn-primary mt-9" data-testid="offer-cta-button">
                                {t("offer.cta")} <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                        <div className="relative">
                            <div className="rounded-3xl overflow-hidden border-4 border-white/10 aspect-[4/3]">
                                <img src={WINDOW_IMG} alt="Schaufenster Reinigung" className="w-full h-full object-cover" />
                            </div>
                            <div className="absolute -bottom-5 -left-5 bg-white text-[#1A365D] rounded-2xl px-5 py-4 shadow-2xl">
                                <div className="text-[10px] uppercase tracking-wider font-bold text-[#16A34A]">Nachher</div>
                                <div className="font-display font-bold text-lg">Streifenfrei klar.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SERVICE AREA */}
            <ServiceArea />

            {/* BOOKING */}
            <section id="booking" className="py-24 lg:py-32 bg-[#F8FAFC]" data-testid="booking-section">
                <div className="max-w-5xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-12">
                        <span className="overline mb-4 inline-block">{t("booking.overline")}</span>
                        <h2 className="font-display font-black text-4xl sm:text-5xl text-[#1A365D] tracking-tight">{t("booking.title")}</h2>
                        <p className="mt-5 text-lg text-[#64748B] max-w-xl mx-auto">{t("booking.subtitle")}</p>
                    </div>
                    <BookingForm />

                    <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-6 text-sm">
                        <a href="tel:0786030294" className="inline-flex items-center gap-2 text-[#1A365D] font-semibold hover:text-[#16A34A]" data-testid="booking-call-link">
                            <Phone className="w-4 h-4" /> 078 603 02 94
                        </a>
                        <span className="hidden md:block text-slate-300">·</span>
                        <a href="https://wa.me/41786030294" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#16A34A] font-semibold hover:underline" data-testid="booking-whatsapp-link">
                            <MessageCircle className="w-4 h-4" /> WhatsApp Offerte
                        </a>
                    </div>
                </div>
            </section>

            <Footer />

            <StickyDiscount />

            {/* Floating WhatsApp */}
            <a
                href="https://wa.me/41786030294"
                target="_blank"
                rel="noreferrer"
                className="fixed bottom-6 right-6 z-40 inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#16A34A] text-white shadow-2xl shadow-[#16A34A]/40 hover:scale-110 transition-transform"
                aria-label="WhatsApp Offerte"
                data-testid="floating-whatsapp-button"
            >
                <MessageCircle className="w-6 h-6" />
            </a>

            {/* Admin/Employee quick link */}
            <Link
                to="/login"
                className="fixed bottom-6 left-6 z-40 hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/90 backdrop-blur border border-slate-200 text-xs font-semibold text-[#1A365D] shadow-lg hover:bg-white"
                data-testid="floating-staff-login-link"
            >
                Mitarbeiter Login
            </Link>
        </div>
    );
}
